import React, { useState, useEffect, useRef } from 'react';
import api from '../api/api';
import { Form, Button, ProgressBar, Toast, ToastContainer, Card, Spinner, ListGroup } from 'react-bootstrap';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useMemo } from 'react';
import GamesSelect from './GameSelect';
import { useGames } from '../hooks/useGames';

// Datei in Chunks schneiden
function sliceFile(file, chunkSize) {
  const chunks = [];
  let off = 0;
  while (off < file.size) {
    const end = Math.min(off + chunkSize, file.size);
    chunks.push(file.slice(off, end));
    off = end;
  }
  return chunks;
}

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

async function startUploadSession(api, file) {
  const { data } = await api.post('/upload/init', {
    filename: file.name,
    size: file.size,
    type: file.type,
  });
  return data.uploadId;
}

async function uploadChunk(api, { uploadId, index, blob }, attempt = 1) {
  const MAX_RETRIES = 5;
  const fd = new FormData();
  fd.append('chunk', blob);
  fd.append('uploadId', uploadId);
  fd.append('index', index);
  try {
    await api.post('/upload/chunk', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  } catch (e) {
    const status = e.response?.status;
    const serverMsg = e.response?.data?.error || e.message;
    console.warn(`[Upload] Chunk ${index} fehlgeschlagen (Versuch ${attempt}/${MAX_RETRIES}, HTTP ${status || '?'}): ${serverMsg}`);
    if (attempt < MAX_RETRIES) {
      await sleep(1000 * attempt);
      return uploadChunk(api, { uploadId, index, blob }, attempt + 1);
    }
    const err = new Error(`Upload-Chunk ${index} fehlgeschlagen nach ${MAX_RETRIES} Versuchen (HTTP ${status || '?'}): ${serverMsg}`);
    err.status = status;
    throw err;
  }
}

async function completeUpload(api, uploadId) {
  const { data } = await api.post('/upload/complete', { uploadId });
  return data; // { success, filename }
}

// Drosselung: Hält die Upload-Geschwindigkeit unter einem gesetzten Limit (MB/s).
// 0 = unbegrenzt.
class UploadThrottler {
  constructor(limitMBs) {
    this.limitBytesPerMs = limitMBs > 0 ? (limitMBs * 1024 * 1024) / 1000 : Infinity;
    this.startTime = null;
    this.totalBytes = 0;
  }
  async throttle(bytes) {
    if (this.limitBytesPerMs === Infinity) return;
    if (this.startTime === null) this.startTime = performance.now();
    this.totalBytes += bytes;
    const expectedMs = this.totalBytes / this.limitBytesPerMs;
    const actualMs = performance.now() - this.startTime;
    const sleepMs = expectedMs - actualMs;
    if (sleepMs > 5) await sleep(sleepMs);
  }
}

async function uploadInChunks(api, file, { parallel = 4, chunkSize = 16 * 1024 * 1024, onProgress, maxSpeedMBs = 0 } = {}) {
  const uploadId = await startUploadSession(api, file);
  const chunks = sliceFile(file, chunkSize);
  let loaded = 0, total = file.size;
  const throttler = new UploadThrottler(maxSpeedMBs);

  const tasks = chunks.map((blob, index) => async () => {
    await uploadChunk(api, { uploadId, index, blob });
    await throttler.throttle(blob.size);
    loaded += blob.size;
    onProgress && onProgress(loaded, total);
  });

  let cursor = 0;
  const workers = Array.from({ length: Math.min(parallel, tasks.length) }, async () => {
    while (cursor < tasks.length) {
      const i = cursor++;
      await tasks[i]();
    }
  });

  await Promise.all(workers);
  return completeUpload(api, uploadId);
}


function UploadForm({ darkMode, cardBgClass, onVideoSaved }) {
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [processingFilename, setProcessingFilename] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState('');
  const [uploadSpeed, setUploadSpeed] = useState('');
  const [uploadSpeedColor, setUploadSpeedColor] = useState('text-success');
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [title, setTitle] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [duration, setDuration] = useState('');
  const { games, refresh: refreshGames, addGame } = useGames();

  const [selectedGame, setSelectedGame] = useState('');

  const [canSave, setCanSave] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]);
  const [speedHistory, setSpeedHistory] = useState([]);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [streamTitlePreview, setStreamTitelPreview] = useState('');

  const [processing, setProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingTimeRemaining, setProcessingTimeRemaining] = useState('');
  const [freeSpace, setFreeSpace] = useState(null);
  const [estimatedVideoMinutes, setEstimatedVideoMinutes] = useState(null);

  const fileInputRef = useRef(null);
  let uploadStartTime = useRef(null);
  const isUploadingRef = useRef(false); // verhindert Hintergrund-Polls während Chunk-Upload
  const statusPollingRef = useRef(null); // Polling-Intervall für Convert-Status (aufgeräumt beim Unmount)

  // Upload-Geschwindigkeitslimit (MB/s), 0 = unbegrenzt, persistent im localStorage
  const [speedLimitMBs, setSpeedLimitMBs] = useState(() => {
    const saved = localStorage.getItem('uploadSpeedLimit');
    return saved !== null ? Number(saved) : 0;
  });
  const handleSpeedLimitChange = (val) => {
    setSpeedLimitMBs(val);
    localStorage.setItem('uploadSpeedLimit', String(val));
  };

  const MAX_STREAM_TITLE_LENGTH = 140;

  const getStreamTitleLength = (title, releaseDate) => {
    const dateObj = releaseDate ? new Date(releaseDate) : new Date();
    const formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${dateObj.getFullYear()}`;
    const preview = `📌 ${formattedDate} - ${title} | 24/7 mRoonix Content`;
    return preview.length;
  };

  const { currentTitleLength, remainingCharacters } = useMemo(() => {
    const length = getStreamTitleLength(title, releaseDate);
    return {
      currentTitleLength: length,
      remainingCharacters: MAX_STREAM_TITLE_LENGTH - length
    };
  }, [title, releaseDate]);


  const fetchDiskUsage = async () => {
    try {
      const res = await api.get('/disk-usage');
      if (res.data) {
        setFreeSpace(res.data.free_bytes);
        setEstimatedVideoMinutes(res.data.estimated_minutes);
      }
    } catch (err) {
      console.error('Fehler beim Abrufen des Speicherplatzes:', err);
    }
  };

  useEffect(() => {
    refreshGames();
    fetchDiskUsage();
  }, []);

  useEffect(() => {
    if (!releaseDate || !title) {
      setStreamTitelPreview('');
      return;
    }

    // Datum formatieren
    const dateObj = new Date(releaseDate);
    const formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${dateObj.getFullYear()}`;

    // Kanalnamen dynamisch eintragen (derzeit manuell)
    const channelName = 'mRoonix';
    const preview = `📌 ${formattedDate} - ${title} | 24/7 ${channelName} Content`;
    setStreamTitelPreview(preview);
  }, [title, releaseDate]);

  const stopStatusPolling = () => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
  };

  const startStatusPolling = (filename) => {
    stopStatusPolling(); // Bestehenden Intervall sauber aufräumen (Memory Leak Fix)
    let errorCount = 0;
    const MAX_ERRORS = 5; // Erst nach 5 aufeinanderfolgenden Fehlern aufgeben

    const fetchStatus = async () => {
      try {
        const status = await api.get(
          `/convert-status/${filename}?t=${Date.now()}`,
          { headers: { 'Cache-Control': 'no-store' } }
        );
        errorCount = 0; // Verbindung wieder hergestellt → Fehlerzähler zurücksetzen
        if (status.data.done) {
          stopStatusPolling();
          setProcessing(false);
          setUploadCompleted(false);
          localStorage.removeItem('processingFilename');
          const stored = JSON.parse(localStorage.getItem('processingMeta') || '{}');
          const entryTitle = stored.title || filename;
          setRecentUploads(prev => [`${entryTitle} (${filename})`, ...prev.slice(0, 2)]);
          localStorage.removeItem('processingMeta');
          if (status.data.error) {
            showToastMessage(`❌ Verarbeitung fehlgeschlagen: ${status.data.error}`, 'danger');
          } else {
            showToastMessage('✅ Video gespeichert!');
            resetForm();
            scrollToTop();
            if (onVideoSaved) onVideoSaved();
          }
        } else {
          setProcessingProgress(status.data.progress);
          setProcessingTimeRemaining(status.data.estimated_remaining);
        }
      } catch (err) {
        errorCount++;
        console.warn(`[Polling] Fehler ${errorCount}/${MAX_ERRORS}:`, err.message);
        if (errorCount >= MAX_ERRORS) {
          stopStatusPolling();
          setProcessing(false);
          showToastMessage('❌ Verbindung zum Server unterbrochen – Statusabruf abgebrochen.', 'danger');
          console.error('Fehler beim Statusabruf (dauerhaft):', err);
        }
      }
    };

    // Sofortiger erster Abruf → kein 1s-Sprung von 0% beim Neuladen
    fetchStatus();
    statusPollingRef.current = setInterval(fetchStatus, 1000);
  };

  useEffect(() => {
    if (!processingFilename) return;

    const startProcessing = async () => {
      setProcessing(true);
      setProcessingProgress(0);
      setProcessingTimeRemaining('');
      setCanSave(false);
      localStorage.setItem('processingFilename', processingFilename);
      localStorage.setItem('processingMeta', JSON.stringify({ title, releaseDate, game: selectedGame }));

      try {
        const releaseDateFormatted = (() => {
          const dateObj = new Date(releaseDate);
          return `${String(dateObj.getDate()).padStart(2, '0')}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${dateObj.getFullYear()}`;
        })();

        const res = await api.post(`/process-upload/${processingFilename}`, {
          title,
          release_date: releaseDateFormatted,
          game: selectedGame
        });

        if (res.data) {
          if (res.data.duration && !duration) setDuration(res.data.duration);
          setTimeout(() => {
            startStatusPolling(processingFilename);
            checkIsProcessing();
          }, 2000);
        }
      } catch (err) {
        setProcessing(false);
        localStorage.removeItem('processingFilename');
        localStorage.removeItem('processingMeta');
        showToastMessage('❌ Fehler beim Start der Verarbeitung!', 'danger');
        console.error('Fehler bei Verarbeitung:', err);
      }
    };

    startProcessing();
  }, [processingFilename]);

  useEffect(() => {
    const restoreProcessing = async () => {
      try {
        const res = await api.get('/is-processing');
        setUploadDisabled(res.data.processing);

        if (res.data.processing) {
          const storedFilename = localStorage.getItem('processingFilename');
          if (storedFilename) {
            // Nur Polling wieder aufnehmen. setProcessingFilename würde den Start-Effekt
            // auslösen und /process-upload erneut aufrufen (Temp-Datei existiert nicht mehr).
            setUploadCompleted(true);
            setProcessing(true);
            startStatusPolling(storedFilename);
          }
        } else {
          // Nichts läuft mehr → localStorage aufräumen falls veraltet
          localStorage.removeItem('processingFilename');
          localStorage.removeItem('processingMeta');
        }
      } catch (err) {
        console.error('Fehler bei /is-processing:', err);
        setUploadDisabled(true);
      }
    };

    restoreProcessing();
    const interval = setInterval(() => {
      if (!isUploadingRef.current) checkIsProcessing();
    }, 5000);
    return () => {
      clearInterval(interval);
      stopStatusPolling();
    };
  }, []);

  const showToastMessage = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      prepareFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      prepareFile(file);
    }
  };

  const preventDefaults = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const prepareFile = (file) => {
    setSelectedFile(file);
    setTitle(file.name.replace(/\.[^/.]+$/, ''));

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    setReleaseDate(`${yyyy}-${mm}-${dd}`);

    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url;
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      const durationInSeconds = video.duration;
      const hours = Math.floor(durationInSeconds / 3600);
      const minutes = Math.floor((durationInSeconds % 3600) / 60);
      const seconds = Math.floor(durationInSeconds % 60);
      const formatted = `${hours} Std ${minutes} Min ${seconds} Sec`;
      setDuration(formatted);
      file.videoDurationMinutes = durationInSeconds / 60;
    };
  };

  const checkIsProcessing = async () => {
    try {
      const res = await api.get('/is-processing');
      setUploadDisabled(res.data.processing);
    } catch (err) {
      console.error('Fehler bei /is-processing:', err);
      setUploadDisabled(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // Direkt die API abfragen statt auf veralteten React-State zu verlassen
    let processingActive = false;
    try {
      const res = await api.get('/is-processing');
      processingActive = !!res.data.processing;
      setUploadDisabled(processingActive);
    } catch (err) {
      console.error('Fehler bei /is-processing:', err);
      processingActive = true;
      setUploadDisabled(true);
    }

    if (processingActive) {
      showToastMessage('⚠️ Eine Verarbeitung läuft bereits. Bitte warte!', 'warning');
      return;
    }

    if (selectedFile?.videoDurationMinutes && estimatedVideoMinutes !== null) {
      if (selectedFile.videoDurationMinutes > estimatedVideoMinutes) {
        const hours = Math.floor(estimatedVideoMinutes / 60);
        const minutes = Math.floor(estimatedVideoMinutes % 60);
        const readable = hours > 0 ? `${hours} Std ${minutes} Min` : `${minutes} Min`;
        showToastMessage(`❌ Nicht genügend Speicher: Nur Platz für ca. ${readable} Video.`, 'danger');
        return;
      }
    }

    try {
      uploadStartTime.current = Date.now();
      isUploadingRef.current = true;
      setUploadProgress(0);
      setEstimatedTime('');
      setUploadSpeed('');
      setSpeedHistory([]);
      setUploadCompleted(false);
      setUploading(true);

      const completeRes = await uploadInChunks(api, selectedFile, {
        parallel: 4,
        chunkSize: 16 * 1024 * 1024, // 16 MB
        maxSpeedMBs: speedLimitMBs,
        onProgress: (loaded, total) => {
          const percent = Math.round((loaded * 100) / total);
          setUploadProgress(percent);

          const elapsed = (Date.now() - uploadStartTime.current) / 1000;
          const speed = loaded / Math.max(elapsed, 0.001);
          const speedMBs = (speed / (1024 * 1024)).toFixed(2);
          setUploadSpeed(`${speedMBs} MB/s`);

          const speedNum = parseFloat(speedMBs);
          setUploadSpeedColor(speedNum > 5 ? 'text-success' : speedNum >= 1 ? 'text-warning' : 'text-danger');
          setSpeedHistory(prev => [...prev.slice(-19), speedNum]);

          const remainingBytes = total - loaded;
          const estimatedSeconds = remainingBytes / Math.max(speed, 1);
          if (!isNaN(estimatedSeconds)) {
            const minutes = Math.floor(estimatedSeconds / 60);
            const seconds = Math.floor(estimatedSeconds % 60);
            setEstimatedTime(`${minutes} Min ${seconds} Sek`);
          }
        }
      });

      const finalFilename = completeRes?.filename || selectedFile.name;
      setUploadCompleted(true);
      setProcessingFilename(finalFilename);

      scrollToTop();
      showToastMessage('✅ Upload abgeschlossen!');
    } catch (err) {
      console.error('Fehler beim Upload:', err);
      const detail = err.status ? ` (HTTP ${err.status})` : '';
      showToastMessage(`❌ Upload fehlgeschlagen${detail}: ${err.message || 'Unbekannter Fehler'}`, 'danger');
    } finally {
      setUploading(false);
      isUploadingRef.current = false;
      setTimeout(() => setSpeedHistory([]), 3000);
    }
  };


  const resetForm = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setEstimatedTime('');
    setUploadSpeed('');
    setSpeedHistory([]);
    setUploadCompleted(false);
    setProcessingFilename('');
    setTitle('');
    setReleaseDate('');
    setDuration('');
    setSelectedGame('');
    setCanSave(false);
    refreshGames();
  };

  return (
    <>
      <ToastContainer className="position-fixed top-0 start-50 translate-middle-x p-3" style={{ zIndex: 9999 }}>
        <Toast onClose={() => setShowToast(false)} show={showToast} bg={toastVariant} delay={3000} autohide>
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Card className={`p-4 shadow-sm mb-5 ${cardBgClass}`} onDragOver={preventDefaults} onDrop={handleDrop}>
        <h4 className="text-center mb-4">
          🎥 Video hochladen{' '}
          {freeSpace !== null && estimatedVideoMinutes !== null && (() => {
            const freeGB = freeSpace / (1024 ** 3);
            const displayValue =
              freeGB >= 1000
                ? `${(freeGB / 1024).toFixed(2)} TB`
                : `${freeGB.toFixed(1)} GB`;

            const minutes = Math.floor(estimatedVideoMinutes);
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            const readableTime =
              hours > 0
                ? `${hours} Std ${remainingMinutes} Min`
                : `${remainingMinutes} Min`;

            return (
              <span
                className="text-muted"
                title={`Noch Speicher für ca. ${readableTime} vorhanden`}
              >
                ({displayValue} frei)
              </span>
            );
          })()}
        </h4>

        <Form.Group controlId="formFile" className="mb-3">
          <Form.Control type="file" onChange={handleFileSelect} ref={fileInputRef} disabled={uploadCompleted || uploading} />
        </Form.Group>

        {selectedFile && !uploadCompleted && (
          <>
            <hr />
            <h5>📄 Videometadaten</h5>

            <Form.Group className="mb-3">
              <Form.Label>Titel:</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={darkMode ? cardBgClass : ''}
              />
            </Form.Group>

            {streamTitlePreview && (
              <div className="mb-3">
                <Form.Text className={darkMode ? 'text-light' : 'text-muted'}>
                  <strong>Streamtitel:</strong> {streamTitlePreview}
                  <br />
                  <span
                    className={
                      currentTitleLength > MAX_STREAM_TITLE_LENGTH
                        ? 'text-danger'
                        : darkMode
                          ? 'text-light'
                          : 'text-muted'
                    }
                  >
                    {currentTitleLength} / {MAX_STREAM_TITLE_LENGTH} Zeichen verwendet (
                    {remainingCharacters < 0
                      ? `${-remainingCharacters} zu viel`
                      : `${remainingCharacters} übrig`}
                    )
                  </span>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-streamtitle-length">
                        Der vollständige Titel (inkl. Datum und Kanal) darf max. {MAX_STREAM_TITLE_LENGTH} Zeichen lang sein.
                      </Tooltip>
                    }
                  >
                    <span style={{ cursor: 'pointer', marginLeft: '8px', color: darkMode ? '#66b0ff' : '#0d6efd', fontWeight: 'bold' }}>
                      ℹ️
                    </span>
                  </OverlayTrigger>
                </Form.Text>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Veröffentlichungsdatum:</Form.Label>
              <Form.Control
                type="date"
                value={releaseDate}
                onChange={(e) => setReleaseDate(e.target.value)}
                className={darkMode ? cardBgClass : ''}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Videolänge:</Form.Label>
              <Form.Control type="text" value={duration} disabled />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Spiel:</Form.Label>
              <GamesSelect
                games={games}
                value={selectedGame}
                onChange={setSelectedGame}
                className={darkMode ? cardBgClass : ''}
                darkMode={darkMode}
                cardBgClass={cardBgClass}
                allowCreate
                onCreate={addGame}
              />
            </Form.Group>
          </>
        )}

        {uploadProgress > 0 && !uploadCompleted && (
          <>
            <ProgressBar animated now={uploadProgress} label={`${uploadProgress}%`} className="mb-3 mt-3" />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div><strong>📊 {uploadProgress}%</strong></div>
              <div className={`fw-bold ${uploadSpeedColor}`}>🚀 {uploadSpeed}</div>
              <div>⏳ {estimatedTime}</div>
            </div>
            <div style={{ height: '100px', backgroundColor: darkMode ? '#343a40' : '#e9ecef', borderRadius: '8px', marginTop: '1rem', overflow: 'hidden', position: 'relative' }}>
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
                <polyline
                  fill="none"
                  stroke={darkMode ? '#0d6efd' : '#198754'}
                  strokeWidth="2"
                  points={speedHistory.map((speed, idx) => `${(idx / 19) * 100},${100 - Math.min(speed * 2, 100)}`).join(' ')}
                />
              </svg>
            </div>
          </>
        )}

        {!uploadCompleted && !uploading && (
          <div className="mt-3 mb-2">
            <Form.Label className="d-flex justify-content-between mb-1">
              <span>🚦 Upload-Geschwindigkeit</span>
              <strong>{speedLimitMBs === 0 ? 'Unbegrenzt' : `${speedLimitMBs} MB/s`}</strong>
            </Form.Label>
            <Form.Range
              min={0}
              max={200}
              step={1}
              value={speedLimitMBs}
              onChange={(e) => handleSpeedLimitChange(Number(e.target.value))}
            />
            <div className="d-flex justify-content-between">
              <small className="text-muted">Unbegrenzt</small>
              <small className="text-muted">200 MB/s</small>
            </div>
          </div>
        )}

        {!uploadCompleted && (
          <Button
            variant="primary"
            className="mt-1 mb-2"
            onClick={handleUpload}
            disabled={
              !selectedFile || uploading || uploadDisabled ||
              !title.trim() || !releaseDate || !selectedGame ||
              currentTitleLength > MAX_STREAM_TITLE_LENGTH
            }
          >
            {uploading
              ? <><Spinner animation="border" size="sm" /> Hochladen...</>
              : uploadDisabled
                ? '⏳ Warten auf laufende Verarbeitung...'
                : (!title.trim() || !releaseDate || !selectedGame)
                  ? '📋 Metadaten ausfüllen...'
                  : '🚀 Upload & Rendern starten'}
          </Button>
        )}

        {uploadDisabled && !uploading && !uploadCompleted && (
          <div className="alert alert-warning mt-2" role="alert">
            ⚠️ Aktuell wird ein Video verarbeitet. Bitte warte, bis die Verarbeitung abgeschlossen ist, bevor du einen neuen Upload startest.
          </div>
        )}

        {uploadCompleted && processing && (
          <div className="my-4">
            <hr />
            <h6>♻️ Video wird gerendert (Twitch-Format)...</h6>
            <ProgressBar
              striped
              animated
              now={Math.max(processingProgress, 2)}
              label={`${processingProgress}%`}
              className="mb-2"
              aria-label="Render-Fortschritt"
            />
            <div className="text-muted">
              ⏳ Geschätzte Restzeit: {processingTimeRemaining || 'wird berechnet …'}
            </div>
            <div className="mt-2">
              <small className="text-muted">📋 Metadaten werden nach Abschluss automatisch gespeichert.</small>
            </div>
          </div>
        )}

        {recentUploads.length > 0 && (
          <div className="mt-4">
            <h6>🕘 Letzte Uploads:</h6>
            <ListGroup>
              {recentUploads.map((item, idx) => (
                <ListGroup.Item key={idx}>{item}</ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}
      </Card>
    </>
  );
}

export default UploadForm;